<script setup>
import { tv } from 'tailwind-variants';
import { computed, ref, watch } from 'vue';

/**
 * @typedef {object} AvatarProps
 * @property {string} [name=''] - The name of the avatar
 * @property {number} [size=28] - The size of the avatar
 * @property {string} [src=''] - The source of the avatar
 * @property {number} [fontSizeRatio=2.5] - The font size ratio of the avatar
 * @property {'circle'|'square'} [variant='circle'] - The shape variant of the avatar
 * @property {'default'|'secondary'} [variantColor='default'] - The color variant of the avatar
 * @property {'online'|'busy'|'offline'|''} [status=''] - The status indicator dot
 * @property {'bottom-left'|'bottom-right'} [statusPosition='bottom-right'] - Position of the status dot
 */

/**
 * @type {AvatarProps}
 */
const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  size: {
    type: [Number, String],
    default: 28,
  },
  src: {
    type: String,
    default: '',
  },
  fontSizeRatio: {
    type: Number,
    default: 2.5,
  },
  variant: {
    type: String,
    default: 'circle',
    validator: value => ['circle', 'square'].includes(value),
  },
  variantColor: {
    type: String,
    default: 'default',
    validator: value => ['default', 'secondary'].includes(value),
  },
  status: {
    type: String,
    default: '',
    validator: value => ['', 'online', 'busy', 'offline'].includes(value),
  },
  statusPosition: {
    type: String,
    default: 'bottom-right',
    validator: value => ['bottom-left', 'bottom-right'].includes(value),
  },
});

const emit = defineEmits(['error']);

const imageError = ref(false);
watch(
  () => props.src,
  () => {
    imageError.value = false;
  }
);

const avatarRecipe = tv({
  slots: {
    root: [
      'relative select-none',
      'font-medium flex items-center justify-center',
      '',
      'leading-[100%] text-center cursor-default',
      'w-[var(--avatar-size)] h-[var(--avatar-size)]',
    ],
    imageContainer: [
      'absolute top-0 left-0 h-full w-full overflow-hidden',
      'w-[var(--avatar-size)] h-[var(--avatar-size)]',
    ],
  },
  variants: {
    variant: {
      circle: {
        root: 'rounded-full',
        imageContainer: 'rounded-full',
      },
      square: {
        root: 'rounded-xl',
        imageContainer: 'rounded-xl',
      },
    },
    variantColor: {
      default: {
        root: 'bg-brand-azure-7 dark:bg-brand-azure-14 text-brand-azure-14 dark:text-brand-azure-7',
      },
      secondary: {
        root: 'bg-brand-gray-7 dark:bg-brand-gray-14 text-brand-gray-12 dark:text-brand-gray-7',
      },
    },
  },
  defaultVariants: {
    variant: 'circle',
    variantColor: 'default',
  },
});

const statusDotRecipe = tv({
  base: [
    'absolute',
    'z-10',
    'rounded-full',
    'shadow-[0_0_0_2px_white]',
    'dark:shadow-[0_0_0_2px_rgb(30,41,59)]',
    'transition-colors duration-300 ease-in-out',
  ],
  variants: {
    status: {
      online: 'bg-brand-green-9',
      busy: 'bg-brand-orange-10',
      offline: 'bg-brand-red-11',
    },
  },
});

const dotSize = computed(() => {
  const size = Number(props.size);
  // Dot size scales with avatar, min 6px, max 12px
  return Math.max(6, Math.min(12, Math.floor(size * 0.25)));
});

const dotPosition = computed(() => {
  const size = Number(props.size);
  const dot = dotSize.value;

  if (props.variant === 'circle') {
    // Untuk circle: posisi di kurva ~45 derajat
    // Offset dihitung agar dot berada di dalam lingkaran
    const horizontalOffset = Math.floor(size * 0.1 - dot * 0.1);
    const bottomOffset = Math.floor(size * 0.02 - dot * 0.1);

    const positionKey =
      props.statusPosition === 'bottom-left' ? 'left' : 'right';

    return {
      bottom: `${bottomOffset}px`,
      [positionKey]: `${horizontalOffset}px`,
    };
  }

  // Untuk square: posisi di pojok dengan sedikit offset
  const positionKey = props.statusPosition === 'bottom-left' ? 'left' : 'right';

  return {
    bottom: '-2px',
    [positionKey]: '-2px',
  };
});

function getInitials(name) {
  if (!name) return '';

  // Remove extra spaces and split by space
  const nameParts = name.trim().replace(/\s+/g, ' ').split(' ');

  // Handle single word names
  if (nameParts.length === 1) {
    const word = nameParts[0];
    // Return first two characters for single word
    return word.length > 1
      ? word.slice(0, 2).toUpperCase()
      : word.toUpperCase();
  }

  // Get first letter of first and last name
  const firstInitial = nameParts[0][0];
  const lastInitial = nameParts[nameParts.length - 1][0];

  return (firstInitial + lastInitial).toUpperCase();
}

const initials = computed(() => getInitials(props.name));

function handleError() {
  imageError.value = true;
  emit('error');
}
</script>

<template>
  <div
    data-component="CsAvatar"
    :style="{
      '--avatar-size': `${props.size}px`,
      'font-size': `${Math.floor(props.size / props.fontSizeRatio)}px`,
    }"
    :class="avatarRecipe({ variant: props.variant }).root()"
  >
    <span v-if="props.name">
      {{ initials }}
    </span>

    <svg
      v-if="!props.name"
      class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full object-cover text-brand-azure-14 dark:text-brand-azure-10"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="10"
        cy="6.5"
        r="2.5"
        stroke="currentColor"
        stroke-width="1.5"
      />
      <path
        d="M5.89814 15H14.1019C14.4761 15 14.6926 14.5758 14.473 14.2727C13.4385 12.8451 11.763 12 10 12C8.23701 12 6.56149 12.8451 5.52701 14.2727C5.30742 14.5758 5.52392 15 5.89814 15Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>

    <span :class="avatarRecipe(props).imageContainer()">
      <img
        v-if="props.src && !imageError"
        :key="props.src"
        :src="props.src"
        :alt="props.name || 'Avatar'"
        loading="lazy"
        class="w-full h-full object-cover"
        @error="handleError"
      />
    </span>

    <!-- Status Dot -->
    <span
      v-if="props.status"
      :key="props.status"
      :class="[statusDotRecipe(props), 'status-dot-animate']"
      :style="{
        ...dotPosition,
        width: `${dotSize}px`,
        height: `${dotSize}px`,
      }"
    />
  </div>
</template>

<style scoped>
.status-dot-animate {
  animation: status-pop 300ms ease-out;
}

@keyframes status-pop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}
</style>
